/**
 * Where the shared menu goes on screen (ADR 0194, decision 3: "the menu
 * stays on screen and flips near the edges"). Arithmetic on rectangles in
 * window coordinates only, so it's tested without a layout engine.
 */

/** Every menu keeps this far clear of the window's edges, in px. */
export const MENU_EDGE = 8;
// A button's menu hangs this far below (or above) it.
const ANCHOR_GAP = 4;
// A submenu overlaps its menu by the menu's padding, and rises by the padding
// and border, so its first item is level with the item that opened it.
const SUB_OVERLAP = 4;
const SUB_RISE = 5;

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Spot {
  left: number;
  top: number;
}

// Within [low, high]; a menu bigger than the window keeps its top left on it.
function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(value, high));
}

function onScreen(spot: Spot, menu: Size, view: Size): Spot {
  return {
    left: clamp(spot.left, MENU_EDGE, view.width - MENU_EDGE - menu.width),
    top: clamp(spot.top, MENU_EDGE, view.height - MENU_EDGE - menu.height),
  };
}

/**
 * At a point, as a right-click opens it: right of and below the pointer,
 * flipped to the left of it or above it when it would cross an edge.
 */
export function placeAtPoint(x: number, y: number, menu: Size, view: Size): Spot {
  const left = x + menu.width > view.width - MENU_EDGE ? x - menu.width : x;
  const top = y + menu.height > view.height - MENU_EDGE ? y - menu.height : y;
  return onScreen({ left, top }, menu, view);
}

/** A submenu: right of its menu, level with its item, or left of it when there's no room. */
export function placeSubmenu(item: Rect, parent: Rect, sub: Size, view: Size): Spot {
  let left = parent.right - SUB_OVERLAP;
  if (left + sub.width > view.width - MENU_EDGE) {
    left = parent.left - sub.width + SUB_OVERLAP;
  }
  return onScreen({ left, top: item.top - SUB_RISE }, sub, view);
}

/**
 * A button's menu: below it, lined up with its left edge (or its right one
 * near the window's right edge), or above it when there's no room below.
 *
 * - `within`: the dialog the button is in. Below or above inside it is
 *   preferred, then below or above anywhere in the window.
 * - `clearTop`: where "above" starts, when that isn't the button's own top
 *   (above a bar the button sits in, clear of the bar's edge).
 */
export function placeFromAnchor(
  anchor: Rect,
  menu: Size,
  view: Size,
  { within = null, clearTop = anchor.top }: { within?: Rect | null; clearTop?: number } = {},
): Spot {
  const low = Math.max(MENU_EDGE, within?.top ?? 0);
  const high = Math.min(view.height - MENU_EDGE, within?.bottom ?? view.height);
  const below = anchor.bottom + ANCHOR_GAP;
  const above = clearTop - ANCHOR_GAP - menu.height;
  let top: number;
  if (below + menu.height <= high) {
    top = below;
  } else if (above >= low) {
    top = above;
  } else if (below + menu.height <= view.height - MENU_EDGE) {
    top = below;
  } else if (above >= MENU_EDGE) {
    top = above;
  } else {
    top = view.height - MENU_EDGE - menu.height;
  }
  const left = anchor.left + menu.width > view.width - MENU_EDGE ? anchor.right - menu.width : anchor.left;
  return onScreen({ left, top }, menu, view);
}
